import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Space,
  Input,
  Typography,
  List,
  Dropdown,
  Button,
  Modal,
  Radio,
  Divider,
  message,
  Popconfirm,
  Tooltip,
  Spin,
} from "antd";
import {
  Phone,
  Shield,
  Search as SearchIcon,
  MoreHorizontal,
  Upload,
  Trash2,
} from "lucide-react";
import { preventCopyProps } from "views/util/protected";
import {
  deleteByType,
  getData,
  saveData,
} from "services/managerial/managerial-service";
import {
  notificationErroInternal,
  notificationErroInternalGeneric,
} from "util/api-utils";
import { useDebounce } from "views/util/debounce";

const { Text } = Typography;

const normalizeKey = (v) => String(v).replace(/\D/g, "");

const parsePhones = (raw) => {
  if (!raw) return [];

  const normalized = String(raw).replace(/[\r\n,;/]+/g, " "); 
  const matches = normalized.match(/(?:\(?\d{2}\)?\s?)?\d{4,5}[-\s]?\d{4}/g) || [];

  return matches
    .map((match) => normalizePhone(match))
    .filter((p) => p.length >= 8);
};

const normalizePhone = (str) => {
  const digits = str.replace(/[^\d]/g, "");

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 9) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  } else if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  return digits;
};

export default function PhonesPanel({ protectedView = false }) {
  const [loading, setLoading] = useState(false);

  const [busca, setBusca] = useState("");
  const [telefones, setTelefones] = useState({
    total: 0,
    items: [],
    size: 20,
    page: 0,
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkMode, setBulkMode] = useState("REPLACE");

  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const listContainerRef = useRef(null);

  const menu = useMemo(
    () => ({
      items: [
        {
          key: "bulk",
          icon: <Upload size={14} />,
          label: "Colar lista de telefones",
          onClick: () => setBulkOpen(true),
        },
        { type: "divider" },
        {
          key: "clear",
          icon: <Trash2 size={14} />,
          label: "Limpar todos",
          danger: true,
          onClick: () => removeAll(),
        },
      ],
    }),
    []
  );

  const handleBulkSave = async () => {
    const list = parsePhones(bulkText);
    if (!list.length) {
      message.warning?.("Nenhum telefone válido encontrado");
      return;
    }

    setLoading(true);

    try {
      const obj = {
        values: list,
        type: bulkMode,
        value: "PHONE",
      };

      await saveData(obj);
      setBulkText("");
      setBulkOpen(false);
      message.success?.(
        bulkMode === "REPLACE" ? "Lista substituída" : "Telefones adicionados"
      );
      findData();
    } catch (error) {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const removeOne = async (id) => {
    const url = "PHONE/" + id;
    await deleteByType(url);
    findData();
  };

  const removeAll = async () => {
    const obj = {
      values: [],
      type: "REPLACE",
      value: "PHONE",
    };

    await saveData(obj);
    findData();
  };
  useEffect(() => {
    findData();
  }, []);

  const findData = async (append = false) => {
    if (!hasMore && append) return;

    setLoading(true);

    try {
      const filter = {
        search: busca,
        page: page,
        size: size,
      };

      const result = await getData("PHONE", filter);
      const newItems = result.items || [];

      setTelefones((prev) => ({
        ...prev,
        total: result.total,
        items: append ? [...prev.items, ...newItems] : newItems,
      }));
      setHasMore(newItems.length === size);
      if (append) setPage((prev) => prev + 1);
      else setPage(0);
    } catch {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const debouncedFindData = useDebounce(findData, 800);

  useEffect(() => {
    debouncedFindData();
  }, [busca]);

  useEffect(() => {
    return () => {
      debouncedFindData.cancel();
    };
  }, [debouncedFindData]);

  const handleLoadMore = () => {
    const container = listContainerRef.current;
    const scrollTopBefore = container.scrollTop;

    findData(true).then(() => {
      setTimeout(() => {
        container.scrollTop = scrollTopBefore;
      }, 0);
    });
  };

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Space>
              <Phone size={18} style={{ marginTop: 4 }} />
              <Text>WhatsApp ({telefones.total})</Text>

              {protectedView && (
                <Tooltip title="Protegido">
                  <Shield size={14} style={{ marginTop: 2 }} />
                </Tooltip>
              )}
            </Space>

            <div
              style={{
                fontSize: 12,
                color: "rgba(0, 0, 0, 0.45)",
                fontWeight: 50,
              }}
            >
              Esse painel contém {telefones.total} whatsapps
            </div>
          </div>
        }
        extra={
          !protectedView && (
            <Dropdown menu={menu} trigger={["click"]}>
              <Button type="text" icon={<MoreHorizontal size={16} />} />
            </Dropdown>
          )
        }
        style={{
          backgroundColor: "#F5E427",

          display: "flex",
          flexDirection: "column",
          minWidth: 300,
          maxHeight: "85vh",
        }}
        bodyStyle={{
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          overflow: "hidden",
        }}
        {...(protectedView ? preventCopyProps(300, "#F5E427") : {})}
      >
        <Input
          allowClear
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Filtrar telefone"
          prefix={<SearchIcon size={14} />}
          style={{ marginBottom: 20, height: "40px" }}
        />

        <div
          ref={listContainerRef}
          className="custom-scroll"
          style={{ flex: 1, overflow: "auto", paddingRight: 20 }}
        >
          <List
            dataSource={telefones.items}
            renderItem={(numero) => (
              <List.Item
                style={{
                  marginBottom: 8,
                  padding: "8px 12px",
                  borderRadius: 10,
                  backgroundColor: "#ffff",

                  boxShadow:
                    "0 1px 1px rgba(0,0,0,0.08), 0 3px 2px rgba(0,0,0,0.18)",
                }}
                actions={[
                  !protectedView && (
                    <Popconfirm
                      title="Remover este telefone?"
                      onConfirm={() => removeOne(numero.id)}
                    >
                      <Trash2 size={14} style={{ cursor: "pointer" }} />
                    </Popconfirm>
                  ),
                ]}
              >
                <Space>
                  <span>{numero.value}</span>
                </Space>
              </List.Item>
            )}
            locale={{ emptyText: "Nenhum telefone" }}
          />
          {hasMore && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button onClick={handleLoadMore}>Carregar mais</Button>
            </div>
          )}
        </div>
      </Card>

      <Modal
        open={bulkOpen}
        title="Colar lista de telefones"
        onCancel={() => setBulkOpen(false)}
        onOk={handleBulkSave}
        okText="Salvar"
        confirmLoading={loading}
      >
        <Text type="secondary">
          Cole números (separados por espaço, vírgula, ponto e vírgula, barra ou
          quebra de linha)
        </Text>
        <Input.TextArea
          rows={6}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          style={{ marginTop: 8 }}
          placeholder="+55 (11) 9xxxx-xxxx; 1199xxxxxxx; +1 (212) 555-0123 ..."
        />
        <Divider />
        <Radio.Group
          value={bulkMode}
          onChange={(e) => setBulkMode(e.target.value)}
        >
          <Radio value="NEW">Adicionar aos existentes</Radio>
          <Radio value="REPLACE">Substituir lista atual</Radio>
        </Radio.Group>
      </Modal>
    </Spin>
  );
}
